import React, { useMemo } from 'react'

import { Field } from 'redux-form'

import Box, { BoxProps } from '@material-ui/core/Box'
import FormLabel from '@material-ui/core/FormLabel'
import ListSubheader from '@material-ui/core/ListSubheader'
import { makeStyles } from '@material-ui/core/styles'

import ListBoxField from '~/components/fields/ListBoxField'
import SearchField from '~/components/fields/SearchField'
import SliderField from '~/components/fields/SliderField'
import ToggleButtonField from '~/components/fields/ToggleButtonField'
import { RenderOptionProps } from '~/components/shared/ListBox/ListBox'
import ListBoxItem from '~/components/shared/ListBox/ListBoxItem'
import Text from '~/components/shared/Text'

import useRangeOptions from '~/hooks/useRangeOptions'
import useSlider from '~/hooks/useSlider'
import EmpriseGroup from '~/services/EmpriseGroup'
import IntlService from '~/services/IntlService'

import ChevronRight from '~/assets/icons/chevron-right.svg?sprite'
import {
  atmIntervals,
  draftIntervalsBoat,
  lengthIntervals,
  lengthIntervalsRV,
  odometerIntervals,
  towWeightIntervals
} from '~/data/intervals'
import {
  numberOfBerths as _numberOfBerths,
  sellerTypeOptions,
  conditionOptions
} from '~/data/options'

import { IS_RV } from '~/brands'
import { MAX_CHOOSE_YEAR } from '~/constants'
import { ListingMeta } from '~/models/Listing/interface'
import { SearchFormValues } from '~/models/Search'

import { filters } from '../MobileFilters/utils'

import RangeSection from './RangeSection'

const formatGroupOptions = (options: any[]) => {
  return options
    .map((option) => {
      const firstLetter = option.label[0].toUpperCase()
      return {
        firstLetter: /[0-9]/.test(firstLetter) ? '#' : firstLetter,
        ...option
      }
    })
    .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))
}

const formatModelsGroupOptions = (options: any[]) =>
  options.sort((a, b) => -b.make.name.localeCompare(a.make.name))

const useStyles = makeStyles(() => ({
  root: {
    '& .ListBox': {
      flex: 1,
      position: 'relative !important',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1,
      '& .MuiAutocomplete-popper': {
        position: 'absolute !important',
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'column',
        bottom: 0,
        top: 0,
        left: 0,
        zIndex: 100
      },
      '& .MuiAutocomplete-root:not(.d-none) ~ .MuiAutocomplete-popper': {
        top: '40px'
      },
      '& .MuiPaper-root': {
        height: '100%'
      }
    },
    '& .ListBox .MuiAutocomplete-listbox': {
      maxHeight: 'none !important',
      height: '100% !important'
    },
    '& .ListBox .MuiAutocomplete-option': {
      minHeight: 'auto !important'
    }
  }
}))

const renderGroup = (params) => {
  return [
    <ListSubheader key={params.key} component="div">
      {params.group}
    </ListSubheader>,
    params.children
  ]
}

export const MobileFormControl: React.FC<
  { label?: React.ReactChild; disableGutters?: boolean } & BoxProps
> = ({ children, label, disableGutters, ...props }) => {
  const classes = useStyles()

  return (
    <Box className={classes.root} {...props}>
      <FormLabel focused={false} filled>
        <Text
          display="inline-block"
          // @ts-ignore
          variant="subtitle1"
          fontSize={14}
          color="primary"
          mb={1}
          ml={!disableGutters && 2}
        >
          {label}
        </Text>
      </FormLabel>
      {children}
    </Box>
  )
}

interface MakeFilterFieldProps {
  mobile?: boolean
  setActiveFilter?: (key: string) => void
  meta?: ListingMeta
}

export const MakeFilterField = ({
  mobile,
  setActiveFilter,
  ...props
}: MakeFilterFieldProps) => {
  const mobileProps = {
    optionSettings: {
      deserialize: true,
      loadOptions: () => EmpriseGroup.getBrands({ with_listings: true }),
      optionValue: 'name',
      formatOptions: formatGroupOptions
    },
    // virtualized: false,
    groupBy: (option) => option.firstLetter,
    renderGroup,
    // size: 'medium',
    placeholder: 'Type your make',
    renderOption: ({ selected, ...props }: RenderOptionProps) => {
      const handleClick = (e) => {
        e.preventDefault()
        selected && e.stopPropagation()
        selected && setActiveFilter(filters.model)
      }

      return (
        <ListBoxItem
          {...props}
          selected={selected}
          rightAdornment={
            <Box
              display="flex"
              alignItems="center"
              ml={1}
              onClick={(e) => handleClick(e)}
            >
              {selected && (
                <Text
                  whiteSpace="nowrap"
                  mr={1.5}
                  color={selected ? 'primary' : 'textSecondary'}
                >
                  Model
                </Text>
              )}
              <ChevronRight width={7} height={12} />
            </Box>
          }
        />
      )
    }
  }

  const currentProps = mobile ? mobileProps : {}

  return (
    <Field
      name="make.name"
      multiple
      component={ListBoxField}
      optionSettings={{
        deserialize: true,
        loadOptions: () => EmpriseGroup.getBrands({ with_listings: true }),
        optionValue: 'name'
      }}
      {...currentProps}
      {...props}
    />
  )
}

interface ModelFilterFieldProps {
  values: any
  mobile?: boolean
  meta?: ListingMeta
  setActiveFilter?: (key: string) => void
}

export const ModelFilterField = ({
  values,
  mobile,
  setActiveFilter,
  ...props
}: ModelFilterFieldProps) => {
  const mobileProps = {
    // size: 'medium',
    placeholder: 'Type your model',
    virtualized: false,
    optionSettings: {
      deserialize: true,
      loadOptions: () =>
        values.make &&
        EmpriseGroup.getModels({
          make_name: values.make?.name as string[]
        }),

      optionValue: 'name',
      formatOptions: formatModelsGroupOptions
    },
    renderOption: ({ selected, ...props }: RenderOptionProps) => {
      return (
        <ListBoxItem
          {...props}
          selected={selected}
          rightAdornment={
            <Box
              display="flex"
              alignItems="center"
              ml={1}
              onClick={(e) => {
                e.preventDefault()
                selected && e.stopPropagation()
                selected && setActiveFilter(filters.series)
              }}
            >
              {selected && (
                <Text
                  whiteSpace="nowrap"
                  mr={1.5}
                  color={selected ? 'primary' : 'textSecondary'}
                >
                  Series
                </Text>
              )}
              <ChevronRight width={7} height={12} />
            </Box>
          }
        />
      )
    },
    groupBy: (option) => option.make.name
  }

  const currentProps = mobile ? mobileProps : {}

  return (
    <Field
      name="make.model"
      multiple
      groupBy={(option) => option.make.name}
      component={ListBoxField}
      virtualized={false}
      optionSettings={{
        deserialize: true,
        loadOptions: () =>
          values.make &&
          EmpriseGroup.getModels({
            make_name: values.make?.name as string[]
          }),
        optionValue: 'name',
        refetchDeps: [values.make?.name],
        formatOptions: formatModelsGroupOptions
      }}
      {...currentProps}
      {...props}
    />
  )
}

interface SeriesFilterFieldProps {
  values: SearchFormValues
}

export const SeriesFilterField = ({
  values,
  ...props
}: SeriesFilterFieldProps) => {
  return (
    <Field
      name="make.series"
      multiple
      component={ListBoxField}
      optionSettings={{
        deserialize: true,
        loadOptions: () =>
          values.make.model &&
          EmpriseGroup.getSeries({
            model_name: values.make.model
          }),
        optionValue: 'name',
        refetchDeps: [values.make?.model]
      }}
      {...props}
    />
  )
}

interface SliderFilterFieldProps {
  values?: any
  mobile?: boolean
  meta: ListingMeta
}

export const PriceFilterField = ({
  meta,
  ...props
}: SliderFilterFieldProps) => {
  const price = useSlider({
    minValue: +meta?.min_price,
    maxValue: +meta?.max_price
  })

  return (
    <Field
      key={`${price.min}_${price.max}`}
      price
      id="price"
      name="price"
      valueLabelDisplay="auto"
      aria-labelledby="price-slider"
      component={SliderField}
      min={price.min}
      max={price.max}
      scale={price.scale}
      step={null}
      defaultValue={[price.min, price.max]}
      getAriaValueText={(value) => IntlService.compactFormat(value)}
      valueLabelFormat={(value) => {
        if (+value === +meta?.min_price) return 'Min'
        if (+value === +meta?.max_price) return 'Max'
        return IntlService.compactFormat(value)
      }}
      disabled={price.min === price.max}
      marks={price.marks}
      normalize={price.scaleRange}
      format={price.parseRange}
      {...props}
    />
  )
}

export const OdometerFilterField = ({
  meta,
  ...props
}: SliderFilterFieldProps) => {
  const odometer = useSlider({
    minValue: +meta?.min_odometer,
    maxValue: +meta?.max_odometer,
    sliderSteps: odometerIntervals
  })

  return (
    <Field
      key={`${odometer.min}_${odometer.max}`}
      price
      id="odometer"
      name="odometer"
      valueLabelDisplay="auto"
      aria-labelledby="odometer-slider"
      component={SliderField}
      min={odometer.min}
      max={odometer.max}
      scale={odometer.scale}
      step={null}
      defaultValue={[odometer.min, odometer.max]}
      getAriaValueText={(value) =>
        IntlService.numberFormat(value, { notation: 'compact' })
      }
      valueLabelFormat={(value) => {
        if (+value === +meta?.min_odometer) return 'Min'
        if (+value === +meta?.max_odometer) return 'Max'

        return IntlService.numberFormat(value, { notation: 'compact' })
      }}
      disabled={odometer.min === odometer.max}
      marks={odometer.marks}
      normalize={odometer.scaleRange}
      format={odometer.parseRange}
      {...props}
    />
  )
}

const range = (startAt: number, end: number): string[] => {
  const newArr = []
  let i = startAt

  while (i <= end) {
    if (i === startAt && i % 10 !== 0) {
      const year = String(i).split('')
      year.pop()
      year.push('0')
      const newYear = +year.join('')
      newArr.push(newYear)
      i = newYear + 10
      // eslint-disable-next-line no-continue
      continue
    }

    newArr.push(i)

    if (i < 1990) {
      i += 10
    } else {
      i += 1
    }
  }
  return newArr.reverse().map((y) => String(y))
}

interface YearFilterFieldProps {
  values?: any
  mobile?: boolean
  meta: ListingMeta
}

export const YearFilterField = ({ meta, ...props }: YearFilterFieldProps) => {
  const yearOptions = useMemo(() => {
    if (!meta?.years) return []

    const minYear = Math.min(
      ...meta?.years.filter((el) => el !== 0 && el >= 1900)
    )

    return range(minYear, MAX_CHOOSE_YEAR)
  }, [meta?.years])

  return (
    <RangeSection
      name="year"
      min={{
        options: yearOptions
      }}
      max={{
        options: yearOptions
      }}
      {...props}
    />
  )
}

export const AdTypeFilterField = ({ ...props }: any) => {
  return (
    <Field
      id="by_condition"
      name="by_condition"
      component={ToggleButtonField}
      size="medium"
      options={conditionOptions.slice(1)}
      {...props}
    />
  )
}

export const LocationFilterField = ({ ...props }: any) => {
  return (
    <Field
      name="location"
      multiple
      component={ListBoxField}
      optionSettings={{
        deserialize: true,
        loadOptions: EmpriseGroup.getLocations,
        optionValue: 'name'
      }}
      {...props}
    />
  )
}

export const BerthFilterField = ({ ...props }: any) => {
  const numberOfBerths = [..._numberOfBerths.slice(0, -1), '10']

  const numberOfBerthsOptions = useRangeOptions({
    options: numberOfBerths
  })

  return (
    <RangeSection
      name="berths"
      min={{
        options: numberOfBerthsOptions.min
      }}
      max={{
        options: numberOfBerthsOptions.max
      }}
      {...props}
    />
  )
}

interface AggregateTrailerMassFilterFieldProps {
  values?: any
  mobile?: boolean
  meta: ListingMeta
}

export const AggregateTrailerMassFilterField = ({
  meta,
  ...props
}: AggregateTrailerMassFilterFieldProps) => {
  const atmOptions = useRangeOptions({
    min: meta?.min_atm,
    max: meta?.max_atm,
    intervals: atmIntervals
  })

  return (
    <RangeSection
      name="atm"
      min={{
        options: atmOptions.min
      }}
      max={{
        options: atmOptions.max
      }}
      {...props}
    />
  )
}

interface TowBallWeightFilterFieldProps {
  values?: any
  mobile?: boolean
  meta: ListingMeta
}

export const TowBallWeightFilterField = ({
  meta,
  ...props
}: TowBallWeightFilterFieldProps) => {
  const towWeightOptions = useRangeOptions({
    min: meta?.min_tow_weight,
    max: meta?.max_tow_weight,
    intervals: towWeightIntervals
  })

  return (
    <RangeSection
      name="tow_weight"
      min={{
        options: towWeightOptions.min
      }}
      max={{
        options: towWeightOptions.max
      }}
      {...props}
    />
  )
}

interface LengthFilterFieldProps {
  values?: any
  mobile?: boolean
  meta: ListingMeta
  change: (field: string, value: any) => void
}

export const LengthFilterField = ({
  meta,
  ...props
}: LengthFilterFieldProps) => {
  const lengthOptions = useRangeOptions({
    min: meta?.min_length,
    max: meta?.max_length,
    intervals: IS_RV ? lengthIntervalsRV : lengthIntervals
  })

  return (
    <RangeSection
      name="length"
      min={{
        options: lengthOptions.min
      }}
      max={{
        options: lengthOptions.max
      }}
      {...props}
    />
  )
}

export const LengthFeetFilterField = ({
  meta,
  ...props
}: LengthFilterFieldProps) => {
  const lengthFeetOptions = useRangeOptions({
    min: meta?.min_length_feet,
    max: meta?.max_length_feet,
    intervals: IS_RV ? lengthIntervalsRV : lengthIntervals
  })

  return (
    <RangeSection
      name="length_feet"
      min={{
        options: lengthFeetOptions.min
      }}
      max={{
        options: lengthFeetOptions.max
      }}
      {...props}
    />
  )
}

export const BoatTrailerLengthFilterField = ({
  meta,
  ...props
}: LengthFilterFieldProps) => {
  const boatTrailerLengthOptions = useRangeOptions({
    min: meta?.min_boat_trailer_length,
    max: meta?.max_boat_trailer_length,
    intervals: lengthIntervalsRV
  })

  return (
    <RangeSection
      name="boat_trailer_length"
      min={{
        options: boatTrailerLengthOptions.min
      }}
      max={{
        options: boatTrailerLengthOptions.max
      }}
      {...props}
    />
  )
}

export const BoatDraftFilterField = ({
  meta,
  ...props
}: LengthFilterFieldProps) => {
  const boatDraftOptions = useRangeOptions({
    min: meta?.min_draft,
    max: meta?.max_draft,
    intervals: draftIntervalsBoat
  })

  return (
    <RangeSection
      name="boat_draft"
      min={{
        options: boatDraftOptions.min
      }}
      max={{
        options: boatDraftOptions.max
      }}
      {...props}
    />
  )
}

export const BoatTrailerLengthFeetFilterField = ({
  meta,
  ...props
}: LengthFilterFieldProps) => {
  const boatTrailerLengthFeetOptions = useRangeOptions({
    min: meta?.min_boat_trailer_length_feet,
    max: meta?.max_boat_trailer_length_feet,
    intervals: lengthIntervalsRV
  })

  return (
    <RangeSection
      name="boat_trailer_length_feet"
      min={{
        options: boatTrailerLengthFeetOptions.min
      }}
      max={{
        options: boatTrailerLengthFeetOptions.max
      }}
      {...props}
    />
  )
}

export const SellerByTypeFilterField = ({ ...props }: any) => {
  return (
    <Field
      id="by_seller_type"
      name="by_seller_type"
      placeholder="Search"
      size="medium"
      component={ToggleButtonField}
      options={sellerTypeOptions}
      optionSettings={{
        optionValue: 'name'
      }}
      {...props}
    />
  )
}

export const ByDealerFilterField = ({ ...props }: any) => {
  return (
    <Field
      id="by_dealer"
      name="by_dealer"
      label="Dealer"
      placeholder="Search"
      component={ListBoxField}
      optionSettings={{
        deserialize: true,
        loadOptions: EmpriseGroup.getDealerOptions,
        optionValue: 'name'
      }}
      {...props}
    />
  )
}

export const KeywordFilterField = ({ ...props }: any) => {
  return (
    <Field
      id="keyword"
      name="keyword"
      component={SearchField}
      placeholder="e.g. registration, ref. code, vin ..."
      size="small"
      {...props}
    />
  )
}

export const ByOfferedServiceFilterField = ({ ...props }: any) => {
  return (
    <Field
      id="by_offered_service"
      name="by_offered_service"
      component={ListBoxField}
      optionSettings={{
        loadOptions: EmpriseGroup.getSellerOfferedServices,
        deserialize: true,
        optionValue: 'name'
      }}
      // multiple
      // hideSearch={true}
      {...props}
    />
  )
}
